/**
 * ============================================================================
 * STORE VALIDATORS
 * ============================================================================
 * 
 * Input validation utilities for store actions
 */

/**
 * Validate ID
 * @param {string} id - ID to validate
 * @param {string} name - Name of the ID field
 * @throws {Error} If ID is invalid
 */
export const validateId = (id, name = 'ID') => {
  if (!id || typeof id !== 'string') {
    throw new Error(`${name} must be a non-empty string`);
  }
  if (id.trim().length === 0) {
    throw new Error(`${name} cannot be empty`);
  }
};

/**
 * Validate array
 * @param {Array} array - Array to validate
 * @param {string} name - Name of the array field
 * @throws {Error} If array is invalid
 */
export const validateArray = (array, name = 'Array') => {
  if (!Array.isArray(array)) {
    throw new Error(`${name} must be an array`);
  }
};

/**
 * Validate object
 * @param {Object} obj - Object to validate
 * @param {string} name - Name of the object field
 * @throws {Error} If object is invalid
 */
export const validateObject = (obj, name = 'Object') => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    throw new Error(`${name} must be an object`);
  }
};

/**
 * Validate pagination
 * @param {Object} pagination - Pagination object
 * @throws {Error} If pagination is invalid
 */
export const validatePagination = (pagination) => {
  validateObject(pagination, 'Pagination');
  if (pagination.page !== undefined && (typeof pagination.page !== 'number' || pagination.page < 1)) {
    throw new Error('Pagination page must be a positive number');
  }
  if (pagination.pageSize !== undefined && (typeof pagination.pageSize !== 'number' || pagination.pageSize < 1)) {
    throw new Error('Pagination pageSize must be a positive number');
  }
  if (pagination.total !== undefined && (typeof pagination.total !== 'number' || pagination.total < 0)) {
    throw new Error('Pagination total must be a non-negative number');
  }
};

/**
 * Validate filters
 * @param {Object} filters - Filters object
 * @throws {Error} If filters are invalid
 */
export const validateFilters = (filters) => {
  validateObject(filters, 'Filters');
};

/**
 * Validate transaction
 * @param {Object} transaction - Transaction object
 * @throws {Error} If transaction is invalid
 */
export const validateTransaction = (transaction) => {
  validateObject(transaction, 'Transaction');
  validateId(transaction.id, 'Transaction ID');
};

/**
 * Validate payment
 * @param {Object} payment - Payment object
 * @throws {Error} If payment is invalid
 */
export const validatePayment = (payment) => {
  validateObject(payment, 'Payment');
  validateId(payment.id, 'Payment ID');
};

/**
 * Validate notification
 * @param {Object} notification - Notification object
 * @throws {Error} If notification is invalid
 */
export const validateNotification = (notification) => {
  validateObject(notification, 'Notification');
  if (notification.id) {
    validateId(notification.id, 'Notification ID');
  }
};

/**
 * Validate user profile
 * @param {Object} profile - User profile object
 * @throws {Error} If profile is invalid
 */
export const validateProfile = (profile) => {
  validateObject(profile, 'Profile');
  if (profile.id) {
    validateId(profile.id, 'Profile ID');
  }
};

/**
 * Validate wallet
 * @param {Object} wallet - Wallet object
 * @throws {Error} If wallet is invalid
 */
export const validateWallet = (wallet) => {
  validateObject(wallet, 'Wallet');
  if (wallet.id) {
    validateId(wallet.id, 'Wallet ID');
  }
};

/**
 * Validate balance
 * @param {Object} balance - Balance object
 * @throws {Error} If balance is invalid
 */
export const validateBalance = (balance) => {
  validateObject(balance, 'Balance');
  if (balance.available !== undefined && typeof balance.available !== 'number') {
    throw new Error('Balance available must be a number');
  }
  if (balance.pending !== undefined && typeof balance.pending !== 'number') {
    throw new Error('Balance pending must be a number');
  }
  if (balance.locked !== undefined && typeof balance.locked !== 'number') {
    throw new Error('Balance locked must be a number');
  }
};

/**
 * Safe execute with validation
 * @param {Function} fn - Function to execute
 * @param {Function} validator - Validator function
 * @param {*} args - Arguments to validate and pass
 * @returns {*} Function result
 * @throws {Error} If validation fails
 */
export const safeExecute = (fn, validator, ...args) => {
  if (validator) {
    args.forEach((arg, index) => {
      try {
        validator(arg);
      } catch (error) {
        throw new Error(`Validation error for argument ${index}: ${error.message}`);
      }
    });
  }
  return fn(...args);
};
